"use client";

import { useEffect, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

interface EmailModalProps {
  isOpen: boolean;
  initialEmail: string | null;
  isSaving: boolean;
  serverError: string | null;
  onClose: () => void;
  onSubmit: (email: string) => Promise<void>;
}

const emailRegex =
  /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

export function EmailModal({
  isOpen,
  initialEmail,
  isSaving,
  serverError,
  onClose,
  onSubmit,
}: EmailModalProps) {
  const [email, setEmail] = useState(initialEmail ?? "");
  const [validationError, setValidationError] = useState<string | null>(null);

  useEffect(() => {
    if (isOpen) {
      setEmail(initialEmail ?? "");
      setValidationError(null);
    }
  }, [isOpen, initialEmail]);

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setValidationError(null);

    const trimmedEmail = email.trim();

    if (!trimmedEmail) {
      setValidationError("Email is required.");
      return;
    }

    if (!emailRegex.test(trimmedEmail)) {
      setValidationError("Enter a valid email address.");
      return;
    }

    await onSubmit(trimmedEmail);
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !isSaving && !open && onClose()}>
      <DialogContent
        className="max-w-md rounded-xl"
        closeOnEscape={!isSaving}
        closeOnInteractOutside={!isSaving}
      >
        <DialogHeader>
          <DialogTitle className="text-center text-xl font-semibold">
            {initialEmail ? "Update Email" : "Set Email"}
          </DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="space-y-2">
            <label className="text-sm text-muted-foreground">
              Enter the email address where you want to receive updates.
            </label>
            <Input
              type="email"
              autoComplete="email"
              placeholder="you@example.com"
              value={email}
              onChange={(event) => setEmail(event.target.value)}
              disabled={isSaving}
            />
            {(validationError || serverError) && (
              <p className="text-sm text-red-500">
                {validationError ?? serverError}
              </p>
            )}
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              disabled={isSaving}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={isSaving}>
              {isSaving ? "Saving..." : initialEmail ? "Update Email" : "Set Email"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

